import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Globe, Heart } from 'lucide-react';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const { favoriteIds } = useFavorites();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img 
              src="/logo.png" 
              alt="Roomia.net" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-home">
              {t('home')}
            </Link>
            <Link to="/listings" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-listings">
              {t('listings')}
            </Link>
            <Link to="/services" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-services">
              Services
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-blog">
              Blog
            </Link>
            <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact">
              {t('contact')}
            </Link>
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            {/* Favorites */}
            {user && (
              <Link to="/favorites" className="relative" data-testid="nav-favorites">
                <Button variant="ghost" size="icon" className="relative">
                  <Heart className="h-5 w-5" />
                  {favoriteIds.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                      {favoriteIds.length}
                    </span>
                  )}
                </Button>
              </Link>
            )}

            {/* Notifications */}
            {user && <NotificationBell />}

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="gap-2"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language.toUpperCase()}
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="user-menu-trigger">
                    <User className="h-4 w-4" />
                    {user.name?.split(' ')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                    <Heart className="h-4 w-4 mr-2" />
                    {language === 'fr' ? 'Mes Favoris' : 'My Favorites'}
                    {favoriteIds.length > 0 && (
                      <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {favoriteIds.length}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    {t('dashboard')}
                  </DropdownMenuItem>
                  {['admin', 'superadmin'].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="h-4 w-4 mr-2" />
                      {t('admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/auth')} data-testid="nav-login">
                  {t('login')}
                </Button>
                <Button onClick={() => navigate('/auth?mode=register')} data-testid="nav-register">
                  {t('register')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/50 bg-background">
          <div className="px-4 py-4 space-y-4">
            <Link
              to="/"
              className="block py-2 text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('home')}
            </Link>
            <Link
              to="/listings"
              className="block py-2 text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('listings')}
            </Link>
            <Link
              to="/services"
              className="block py-2 text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              Services
            </Link>
            <Link
              to="/blog"
              className="block py-2 text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="block py-2 text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('contact')}
            </Link>

            <div className="pt-4 border-t border-border">
              <Button
                variant="outline"
                className="w-full mb-2 gap-2"
                onClick={toggleLanguage}
              >
                <Globe className="h-4 w-4" />
                {language === 'fr' ? 'English' : 'Français'}
              </Button>

              {user ? (
                <>
                  <Button
                    variant="outline"
                    className="w-full mb-2"
                    onClick={() => {
                      navigate('/dashboard');
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('dashboard')}
                  </Button>
                  {['admin', 'superadmin'].includes(user.role) && (
                    <Button
                      variant="outline"
                      className="w-full mb-2"
                      onClick={() => {
                        navigate('/admin');
                        setMobileMenuOpen(false);
                      }}
                    >
                      {t('admin')}
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full mb-2"
                    onClick={() => {
                      navigate('/auth');
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('login')}
                  </Button>
                  <Button
                    className="w-full"
                    onClick={() => {
                      navigate('/auth?mode=register');
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t('register')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
